import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Account } from '@wb/core/interfaces/account';
import { Admin } from '@wb/core/interfaces/admin';
import {
  AccountInput,
  AccountInsert,
  AccountSettingsUpdateGQL,
  AccountSettingsUpdateInput,
  AccountSettingsUpdateResponse,
  CreateAccountGQL,
  PublishAccountConfigGQL,
  UpdateAccountGQL,
} from '@wb/core/schema/mutation-definitions/accounts.graphql';
import {
  AccountAdminsGQL,
  AccountAdminsQueryVariables,
  AccountSalespeopleGQL,
  AccountsGQL,
  AccountsResponse,
  AdminsResponse,
  ChildAccountMapsGQL,
  ChildMapsResponse,
  RelatedAccount,
  SalesPersonResponse,
} from '@wb/core/schema/query-definitions/accounts.graphql';
import { QueryRef } from 'apollo-angular';
import { BehaviorSubject, Observable, ReplaySubject, Subject, firstValueFrom } from 'rxjs';
import { map, single, take, takeUntil } from 'rxjs/operators';
import { IsUserSelectableGQL } from '../schema/query-definitions/admin.graphql';

@Injectable({
  providedIn: 'root',
})
export class AccountService implements OnDestroy {
  public accountLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private accountAdminsQueryRef: QueryRef<AdminsResponse, AccountAdminsQueryVariables> | undefined;

  private destroyed$: Subject<void> = new Subject<void>();

  private accounts$: ReplaySubject<Account[]> = new ReplaySubject<Account[]>(1);

  private admins$: ReplaySubject<Admin[] | undefined> = new ReplaySubject<Admin[] | undefined>(1);

  private selectedAccount$: BehaviorSubject<Account | undefined> = new BehaviorSubject<Account | undefined>(undefined);

  private aggregatorAccountEnabled: BehaviorSubject<boolean | undefined> = new BehaviorSubject<boolean | undefined>(
    undefined,
  );

  private aggregatorChildAccounts$: BehaviorSubject<RelatedAccount[]> = new BehaviorSubject<RelatedAccount[]>([]);

  private accountNotFound$: Subject<void> = new Subject<void>();

  constructor(
    private readonly accountsGQL: AccountsGQL,
    private readonly accountAdminsGQL: AccountAdminsGQL,
    private readonly isSelectableGQL: IsUserSelectableGQL,
    private readonly accountSettingsUpdateGQL: AccountSettingsUpdateGQL,
    private readonly publishAccountConfigGQL: PublishAccountConfigGQL,
    private readonly childAccountMapsGQL: ChildAccountMapsGQL,
    private readonly createAccountGQL: CreateAccountGQL,
    private readonly updateAccountGQL: UpdateAccountGQL,
    private readonly accountSalespeopleGQL: AccountSalespeopleGQL,
  ) {}

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /**
   * Loads in accounts inclusive of a list of allowable account UIDs
   * This should only be used once per auth state change
   * @param uids
   */
  loadAccounts(uids?: string[]): Promise<Account[] | undefined> {
    return new Promise((resolve, reject) => {
      this.accountLoading$.next(true);

      // Resets the accounts list
      this.accounts$.next([]);

      let where;

      if (uids && uids.length > 0) {
        where = { uids };
      }

      // Request the account list filtered by allowable UID
      this.accountsGQL
        .fetch({ where, limit: 0, orderBy: ['account', 'asc'] }, { fetchPolicy: 'network-only' })
        .pipe(
          map((result) => result.data),
          takeUntil(this.destroyed$),
        )
        .subscribe({
          next: (response: AccountsResponse) => {
            const result = response.accounts.success ? response.accounts.accounts : [];
            this.accounts$.next(result);
            this.accountLoading$.next(false);
            resolve(result);
          },
          error: (err: HttpErrorResponse) => {
            this.accounts$.next([]);
            if (err) {
              console.error(err);
            }
            reject();
          },
        });
    });
  }

  get accounts(): Observable<Account[]> {
    return this.accounts$.asObservable();
  }

  get admins(): Observable<Admin[] | undefined> {
    return this.admins$.asObservable();
  }

  get selectedAccount(): Observable<Account | undefined> {
    return this.selectedAccount$.asObservable();
  }

  get getAggregatorAccountEnabled(): Observable<boolean | undefined> {
    return this.aggregatorAccountEnabled.asObservable();
  }

  get getAggregatorChildAccounts(): Observable<RelatedAccount[]> {
    return this.aggregatorChildAccounts$.asObservable();
  }

  get accountNotFound(): Observable<void> {
    return this.accountNotFound$.asObservable();
  }

  refetchAccount() {
    const accountUid = localStorage.getItem('accountUid');
    if (accountUid) {
      this.selectAccount(accountUid);
    }
  }

  /**
   * Selects an account by UID from the currently loaded list
   * @param accountUid
   */
  async selectAccount(accountUid: string): Promise<void> {
    const selectedAccount: Account | undefined = await new Promise((resolve) => {
      this.accounts$.pipe(take(1)).subscribe((accounts: Account[]) => {
        let selected: Account | undefined;
        if (accounts.length > 0) {
          selected = accounts.find((account: Account) => account.uid === accountUid);
          if (!selected) {
            console.error('[error] selected account not available', accountUid);
            this.accountNotFound$.next();
          }
        }
        resolve(selected);
      });
    });

    if (selectedAccount) {
      this.selectedAccount$.next(selectedAccount);
      localStorage.setItem('accountUid', selectedAccount.uid);

      // Marks the current account as an aggregator if selected
      this.aggregatorAccountEnabled.next(
        selectedAccount.settings.find((setting) => setting.key === 'aggregator_account')?.value === '1',
      );
    }

    this.admins$.next(undefined);
  }

  get currentUid() {
    return localStorage.getItem('accountUid');
  }

  resetAccountData() {
    this.accounts$.next([]);
  }

  resetSelectedAccountData() {
    this.selectedAccount$.next(undefined);
    localStorage.removeItem('accountUid');
  }

  createAccount(input: AccountInsert): Promise<String | HttpErrorResponse> {
    return new Promise((resolve) => {
      this.createAccountGQL
        .mutate({ input }, { fetchPolicy: 'network-only' })
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: async (response) => {
            const accountUid = response.data?.insertAccount?.account?.uid;
            if (accountUid) {
              await this.selectAccount(accountUid);
              resolve(accountUid);
            } else {
              resolve(new HttpErrorResponse({ error: 'Account creation failed' }));
            }
          },
          error: (err: HttpErrorResponse) => {
            resolve(err);
          },
        });
    });
  }

  updateAccount(accountUid: string, input: AccountInput): Promise<String | HttpErrorResponse> {
    return new Promise((resolve) => {
      this.updateAccountGQL
        .mutate({ uid: accountUid, ...input }, { fetchPolicy: 'network-only' })
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: () => {
            resolve(accountUid);
          },
          error: (err: HttpErrorResponse) => {
            resolve(err);
          },
        });
    });
  }

  filterUsers(queryFilter: AccountAdminsQueryVariables) {
    if (this.accountAdminsQueryRef === undefined && this.selectedAccount$.value !== undefined) {
      this.accountAdminsQueryRef = this.accountAdminsGQL.watch(queryFilter, {
        fetchPolicy: 'network-only',
      });

      this.accountAdminsQueryRef.valueChanges
        .pipe(
          map((result) => result.data),
          takeUntil(this.destroyed$),
        )
        .subscribe({
          next: async (response: AdminsResponse) => {
            if (this.selectedAccount$.value) {
              const accounts: Account[] = await firstValueFrom(this.accounts);

              const admins: Admin[] = response.admins.admins.map(
                (admin: Admin) =>
                  ({
                    ...admin,
                    account: accounts.find((account: Account) => account.uid === admin.accountUid)?.account ?? '',
                  } as Admin),
              );

              this.admins$.next(response.admins.success ? admins : []);
            }
          },
          error: (err: HttpErrorResponse) => {
            if (this.selectedAccount$.value) {
              this.admins$.next([]);
            }
            if (err) {
              console.error(err);
            }
          },
        });
    } else if (this.accountAdminsQueryRef && this.selectedAccount$.value) {
      this.accountAdminsQueryRef.setVariables(queryFilter);
    }
  }

  isUserSelectable(userUid: string, userFullName: string): Promise<boolean> {
    return new Promise((resolve) => {
      if (!userFullName) {
        resolve(false);
      }

      this.isSelectableGQL
        .fetch({ uid: userUid }, { fetchPolicy: 'network-only' })
        .pipe(
          single(),
          map((result) => result.data.isSelectable),
          takeUntil(this.destroyed$),
        )
        .subscribe((response) => {
          resolve(response.selectable);
        });
    });
  }

  public loadAggregatorChildAccounts(accountUid: string) {
    this.childAccountMapsGQL
      .fetch({ uid: accountUid }, { fetchPolicy: 'network-only' })
      .pipe(
        map((result) => result.data),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (value: ChildMapsResponse) => {
          this.aggregatorChildAccounts$.next(value.childAccountMaps.childAccounts);
        },
        error: (err: HttpErrorResponse) => console.error(err),
      });
  }

  public updateAccountSettings(
    accountUid: string,
    accountSettingsInput: AccountSettingsUpdateInput[],
  ): Promise<AccountSettingsUpdateResponse | HttpErrorResponse> {
    return new Promise((resolve) => {
      this.accountSettingsUpdateGQL
        .mutate({ uid: accountUid!, accountSettingsInput }, { fetchPolicy: 'network-only' })
        .pipe(
          map((response) => response.data),
          takeUntil(this.destroyed$),
        )
        .subscribe({
          next: async (response?: AccountSettingsUpdateResponse | null) => {
            // get updated account with new settings
            if (response) {
              await this.loadAccounts();
              await this.selectAccount(accountUid);
              resolve(response);
            }
          },
          error: (error: HttpErrorResponse) => {
            console.error(error);
            // Return the error response in case a toast message is needed
            resolve(error);
          },
        });
    });
  }

  publishAccountConfig(accountUid: string) {
    this.publishAccountConfigGQL
      .mutate({ uid: accountUid }, { fetchPolicy: 'network-only' })
      .pipe(
        map((result) => result.data),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        error: (error: HttpErrorResponse) => {
          console.error(error);
        },
      });
  }

  getAccountSalespeople(accountUid: string) {
    try {
      return new Promise<SalesPersonResponse>((resolve, reject) => {
        this.accountSalespeopleGQL
          .fetch(
            {
              accountUid: accountUid,
            },
            { fetchPolicy: 'network-only' },
          )
          .pipe(
            map((response: any) => {
              return response.data;
            }),
          )
          .subscribe(
            (responseData: SalesPersonResponse) => {
              resolve(responseData);
            },
            (error: any) => {
              console.error(error);
              reject(error);
            },
          );
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  }
}
